/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useState } from 'react';
import { ThemeProvider } from '@oneaudi/unified-web-common';

import type { AudiFootnoteReferenceServiceInterfaceV3 } from '@oneaudi/footnote-reference-service';
import type { AudiFootnoteServiceInterfaceV1 } from '@oneaudi/footnote-service';
import type { ScopedDisclaimerManager } from '@volkswagen-onehub/disclaimer-manager';
import styled from 'styled-components';
import type { DynamicFootnoteServiceV1 } from '@oneaudi/dynamic-footnote-service';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import { fetchContent } from '@oneaudi/falcon-tools';
import { ProgressIndicator } from '@oneaudi/unified-web-components';
import type { Footnote } from '../../types/footnotes-response.type';
import AudiFootnoteEngine from '../audi-footnote-engine/audi-footnote-engine';
import type {
  CollectionContentHeadless,
  FefaProps,
  FootnoteFields,
} from '../../utils/normalize-falcon-data';
import { normalizeContentHeadlessCollection } from '../../utils/normalize-falcon-data';
import type { CustomTheme } from '../../../@types/theme';

export interface AudiFootnoteEngineContainerProps {
  apiUrls?: string[];
  audiUiTheme: CustomTheme;
  disclaimerManager: ScopedDisclaimerManager;
  disableBottomSpacing: boolean;
  disableSideSpacing: boolean;
  error?: Error | null;
  isServer?: boolean;
  layerElementClassName?: string;
  referenceServiceManager: AudiFootnoteReferenceServiceInterfaceV3;
  configProvider: GfaServiceConfigProviderV1;
  footnoteService?: AudiFootnoteServiceInterfaceV1;
  dynamicFootnoteService?: DynamicFootnoteServiceV1;
  featureAppId: string;
  footnoteData?: FefaProps;
  isOneLayer?: boolean;
  hasBackground?: boolean;
}

const LoadingWrapper = styled.div`
  text-align: center;
`;

const AudiFootnoteEngineContainer: React.FC<AudiFootnoteEngineContainerProps> = (props) => {
  const {
    apiUrls,
    audiUiTheme,
    disableBottomSpacing,
    disableSideSpacing,
    disclaimerManager,
    error,
    featureAppId,
    footnoteService,
    isServer,
    layerElementClassName,
    referenceServiceManager,
    footnoteData,
    dynamicFootnoteService,
    configProvider,
    isOneLayer,
    hasBackground,
  } = props;
  const [componentError, setComponentError] = useState<Error>(error as Error);
  const [componentData, setComponentData] = useState<Footnote[] | null>(null);
  const [fnData, setfnData] = useState<FootnoteFields[] | null | FefaProps>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  let contentApiConfig = {};
  if (typeof window !== 'undefined') {
    contentApiConfig = configProvider?.audi['falcon-content-api'];
  }

  function hasData<T>(array: T[] | undefined): array is T[] {
    return Array.isArray(array) && array.length > 0;
  }
  const hasFootnoteCollection = hasData(footnoteData?.footnotes);
  const getFootnotes = async (urls: string[]) => {
    const responses = await Promise.all(
      urls?.map(async (url) => {
        try {
          // @ts-expect-error cannot change content fragment type
          const response: CollectionContentHeadless = await fetchContent(
            url.trim(),
            //@ts-expect-error object is empty in ssr
            contentApiConfig,
          );

          return normalizeContentHeadlessCollection(response);
        } catch (err) {
          console.error(`Error fetching content for URL: ${url}`, err);
          return []; // Return a default value (e.g., empty array) in case of an error
        }
      }),
    );
    const combinedFootnotes: Footnote[] = responses.flat();
    setfnData(combinedFootnotes);
  };

  useEffect(() => {
    if (isOneLayer) {
      // @ts-expect-error cannot change content fragment type
      getFootnotes(apiUrls);
    }
    if (footnoteData?.urls && !hasFootnoteCollection) {
      getFootnotes(footnoteData?.urls);
    }
  }, [footnoteData?.urls, isOneLayer]);

  useEffect(() => {
    if (footnoteService) {
      try {
        footnoteService.registerCallback((footnotes) => {
          setComponentData(footnotes);
        });
      } catch (e /* istanbul ignore next */) {
        console.warn(e);
      }
    }

    return (): void => {
      if (footnoteService) {
        footnoteService.unregisterCallback();
      }
    };
  }, [footnoteService]);

  useEffect(() => {
    if (componentError === null && !isInitialized && footnoteService) {
      if (apiUrls && !isOneLayer) {
        /* in headfull cms, footnotes should be retrieved on client side by using API url */
        (async (): Promise<void> => {
          footnoteService.init(apiUrls);
          const initialized = await footnoteService.isInitialized();

          if (initialized) {
            const data = footnoteService.getData();
            setComponentData(data);
            setIsInitialized(true);
          } else {
            const error_ = Error('[footnote-engine] Could not initialize footnote-service.');
            setComponentError(error_);
          }
        })();
      } else if (hasFootnoteCollection || fnData !== null) {
        /* in headless cms, footnotes should be retrieved using content-service */
        const data = fnData || footnoteData?.footnotes;
        try {
          // @ts-expect-error incompatible types
          footnoteService?.initWithFootnotes(data);
          const initialized = Promise.resolve(footnoteService.isInitialized());
          initialized.then((init) => {
            if (init) {
              const finalData = footnoteService.getData();
              setComponentData(finalData);
              setIsInitialized(true);
            } else {
              const error_ = Error('[footnote-service] could not initialize footnote-service.');
              setComponentError(error_);
            }
          });
        } catch (e) {
          console.warn('[fa-footnote-engine] Failed to initialize, falling back to raw data', e);
          // @ts-expect-error incompatible types
          setComponentData(data);
          setIsInitialized(true);
        }
      } else {
        console.log('[fa-footnote-engine] No data source available yet, waiting...');
      }
    }
  }, [
    apiUrls,
    componentError,
    footnoteService,
    isServer,
    footnoteData,
    fnData,
    isOneLayer,
    hasFootnoteCollection,
    isInitialized,
  ]);

  if (componentError !== null) {
    // TODO style error and add error message
    return <div>Error</div>;
  }

  return (
    <ThemeProvider theme={audiUiTheme}>
      {componentData === null || isServer ? (
        <LoadingWrapper>
          <ProgressIndicator
            aria-label="Loading"
            colorScheme="inverted"
            data-testid="fefa-progress-indicator"
            label="Loading.."
            size="md"
            type="infinite"
            variant="spinner"
          />
        </LoadingWrapper>
      ) : (
        <AudiFootnoteEngine
          disableBottomSpacing={disableBottomSpacing}
          disableSideSpacing={disableSideSpacing}
          disclaimerManager={disclaimerManager}
          isOneLayer={isOneLayer}
          featureAppId={featureAppId}
          footnotes={componentData}
          layerElementClassName={layerElementClassName}
          referenceServiceManager={referenceServiceManager}
          replaceFootnotes={footnoteService?.replaceFootNotes}
          dynamicFootnoteService={dynamicFootnoteService}
          hasBackground={hasBackground}
        />
      )}
    </ThemeProvider>
  );
};

export default AudiFootnoteEngineContainer;
